import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers,
  Database,
  Clock,
  HardDrive,
  FileCode,
  Archive,
  RefreshCw,
  Loader2,
  Zap,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface MSSQLTempDBViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[140px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MSSQLTempDBView: React.FC<MSSQLTempDBViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [tempdbData, setTempdbData] = useState<any>(null);

  // Hover states for legends
  const [hoveredBreakdown, setHoveredBreakdown] = useState<string | null>(null);
  const [hoveredVersionActivity, setHoveredVersionActivity] = useState<string | null>(null);
  // const [hoveredInternal, setHoveredInternal] = useState<string | null>(null);
  // const [hoveredUser, setHoveredUser] = useState<string | null>(null);
  // const [hoveredVersionStore, setHoveredVersionStore] = useState<string | null>(null);
  // const [hoveredMixedExtent, setHoveredMixedExtent] = useState<string | null>(null);
  const [hoveredFreeSpace, setHoveredFreeSpace] = useState<string | null>(null);
  const [hoveredTxn, setHoveredTxn] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      clusted_id: activeCluster,
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/mssql/tempdb', { params });
      setTempdbData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MSSQLTempDBView fetch error:', err);
      setTempdbData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels = tempdbData?.labels || [];
  const series = tempdbData?.series || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);

  const formattedData = labels.map((_: string, idx: number) => {
    const internalMb = Number(series.mssql_tempdb_internal_mb?.[idx] ?? 0);
    const userMb = Number(series.mssql_tempdb_user_mb?.[idx] ?? 0);
    const versionStoreMb = Number(series.mssql_tempdb_version_store_mb?.[idx] ?? 0);
    const mixedExtentMb = Number(series.mssql_tempdb_mixed_extent_mb?.[idx] ?? 0);
    const freeMb = Number(series.mssql_tempdb_free_mb?.[idx] ?? 0);
    const versionGenKb = Number(series.mssql_version_gen_kb_total?.[idx] ?? 0);
    const versionCleanupKb = Number(series.mssql_version_cleanup_kb_total?.[idx] ?? 0);
    const longestTxnSec = Number(series.mssql_longest_txn_sec?.[idx] ?? 0);

    const totalUsedMb = internalMb + userMb + versionStoreMb + mixedExtentMb;

    return {
      time: timeLabels[idx],
      internal_mb: Number(internalMb.toFixed(2)),
      user_mb: Number(userMb.toFixed(2)),
      version_store_mb: Number(versionStoreMb.toFixed(2)),
      mixed_extent_mb: Number(mixedExtentMb.toFixed(2)),
      free_mb: Number(freeMb.toFixed(2)),
      total_used_mb: Number(totalUsedMb.toFixed(2)),
      version_gen_kb: Number(versionGenKb.toFixed(2)),
      version_cleanup_kb: Number(versionCleanupKb.toFixed(2)),
      longest_txn_sec: Number(longestTxnSec.toFixed(2)),
    };
  });

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0
      )
    );
  };

  const summary = tempdbData?.summary;

  const formatNumber = (num: number | undefined | null, decimals = 2) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    const val = Number(num);
    return Number.isInteger(val)
      ? val.toLocaleString()
      : val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
  };

  const formatMB = (val: number | undefined | null) => {
    if (val === undefined || val === null || isNaN(val)) return '0 MB';
    const num = Number(val);
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} MB`;
  };

  const formatKB = (val: number | undefined | null) => {
    if (val === undefined || val === null || isNaN(val)) return '0 KB';
    const num = Number(val);
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} KB`;
  };

  const getSummaryVal = useCallback((catKey: string, latestKey: string) => {
    if (summary?.categories?.[catKey] !== undefined && summary?.categories?.[catKey] !== null) {
      return Number(summary.categories[catKey]);
    }
    if (summary?.latest?.[latestKey] !== undefined && summary?.latest?.[latestKey] !== null) {
      return Number(summary.latest[latestKey]);
    }
    if (summary?.latest?.[catKey] !== undefined && summary?.latest?.[catKey] !== null) {
      return Number(summary.latest[catKey]);
    }
    const arr = series[latestKey];
    if (Array.isArray(arr) && arr.length > 0) {
      const last = arr[arr.length - 1];
      return last !== undefined && last !== null ? Number(last) : 0;
    }
    return 0;
  }, [summary, series]);

  const summaryCards = useMemo(() => {
    const userMb = getSummaryVal('user_objects_mb', 'mssql_tempdb_user_mb');
    const internalMb = getSummaryVal('internal_objects_mb', 'mssql_tempdb_internal_mb');
    const versionStoreMb = getSummaryVal('version_store_mb', 'mssql_tempdb_version_store_mb');
    const mixedExtentMb = getSummaryVal('mixed_extents_mb', 'mssql_tempdb_mixed_extent_mb');
    const freeMb = getSummaryVal('free_space_mb', 'mssql_tempdb_free_mb');
    const versionGenKb = getSummaryVal('version_gen_kb_total', 'mssql_version_gen_kb_total');
    const versionCleanupKb = getSummaryVal('version_cleanup_kb_total', 'mssql_version_cleanup_kb_total');
    const longestTxnSec = getSummaryVal('longest_txn_sec', 'mssql_longest_txn_sec');

    let totalMb = summary?.total;
    if (totalMb === undefined || totalMb === null) {
      totalMb = userMb + internalMb + versionStoreMb + mixedExtentMb + freeMb;
    } else {
      totalMb = Number(totalMb);
    }

    return [
      {
        label: 'Total Space',
        value: formatMB(totalMb),
        icon: Database,
        cardBg: 'from-blue-950/20 to-gray-900/80 border-blue-500/30',
        color: 'text-blue-400',
      },
      {
        label: 'User Objects',
        value: formatMB(userMb),
        icon: Layers,
        cardBg: 'from-indigo-950/20 to-gray-900/80 border-indigo-500/30',
        color: 'text-indigo-400',
      },
      {
        label: 'Internal Objects',
        value: formatMB(internalMb),
        icon: FileCode,
        cardBg: 'from-purple-950/20 to-gray-900/80 border-purple-500/30',
        color: 'text-purple-400',
      },
      {
        label: 'Version Store',
        value: formatMB(versionStoreMb),
        icon: Archive,
        cardBg: 'from-amber-950/20 to-gray-900/80 border-amber-500/30',
        color: 'text-amber-400',
      },
      {
        label: 'Mixed Extents',
        value: formatMB(mixedExtentMb),
        icon: Zap,
        cardBg: 'from-cyan-950/20 to-gray-900/80 border-cyan-500/30',
        color: 'text-cyan-400',
      },
      {
        label: 'Free Space',
        value: formatMB(freeMb),
        icon: HardDrive,
        cardBg: 'from-emerald-950/20 to-gray-900/80 border-emerald-500/30',
        color: 'text-emerald-400',
      },
      {
        label: 'Version Gen',
        value: formatKB(versionGenKb),
        icon: RefreshCw,
        cardBg: 'from-teal-950/20 to-gray-900/80 border-teal-500/30',
        color: 'text-teal-400',
      },
      {
        label: 'Version Cleanup',
        value: formatKB(versionCleanupKb),
        icon: RefreshCw,
        cardBg: 'from-orange-950/20 to-gray-900/80 border-orange-500/30',
        color: 'text-orange-400',
      },
      {
        label: 'Longest Txn',
        value: `${formatNumber(longestTxnSec)} s`,
        icon: Clock,
        cardBg: 'from-rose-950/20 to-gray-900/80 border-rose-500/30',
        color: 'text-rose-400',
      },
    ];
  }, [summary, getSummaryVal]);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.15;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Layers className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-white">Temporary DB</h2>
        </div>
      </div>

      {/* Top Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
        {summaryCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div
              key={idx}
              className={`bg-gradient-to-br ${card.cardBg} border rounded-xl p-3 shadow-md flex flex-col justify-between`}
            >
              <div className="flex items-center justify-between text-[11px] text-gray-200 font-semibold mb-1">
                <span>{card.label}</span>
                <Icon className={`w-3.5 h-3.5 ${card.color}`} />
              </div>
              <div className={`text-base font-bold font-mono ${card.color}`}>
                {card.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 1: Space Allocation Overview & Version Store Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 1: TempDB Space Allocation Breakdown */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Database className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              TempDB Space Allocation (MB)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorUserMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorInternalMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorVersionStoreMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorMixedExtentMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredBreakdown}
                        onHoverSeries={setHoveredBreakdown}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="user_mb"
                    name="User Objects (MB)"
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredBreakdown, 'user_mb')}
                    fillOpacity={getOpacity(hoveredBreakdown, 'user_mb') * 0.4}
                    fill="url(#colorUserMb)"
                    strokeWidth={hoveredBreakdown === 'user_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="internal_mb"
                    name="Internal Objects (MB)"
                    stroke="#a855f7"
                    strokeOpacity={getOpacity(hoveredBreakdown, 'internal_mb')}
                    fillOpacity={getOpacity(hoveredBreakdown, 'internal_mb') * 0.4}
                    fill="url(#colorInternalMb)"
                    strokeWidth={hoveredBreakdown === 'internal_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="version_store_mb"
                    name="Version Store (MB)"
                    stroke="#f59e0b"
                    strokeOpacity={getOpacity(hoveredBreakdown, 'version_store_mb')}
                    fillOpacity={getOpacity(hoveredBreakdown, 'version_store_mb') * 0.4}
                    fill="url(#colorVersionStoreMb)"
                    strokeWidth={hoveredBreakdown === 'version_store_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="mixed_extent_mb"
                    name="Mixed Extents (MB)"
                    stroke="#06b6d4"
                    strokeOpacity={getOpacity(hoveredBreakdown, 'mixed_extent_mb')}
                    fillOpacity={getOpacity(hoveredBreakdown, 'mixed_extent_mb') * 0.4}
                    fill="url(#colorMixedExtentMb)"
                    strokeWidth={hoveredBreakdown === 'mixed_extent_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No TempDB space allocation telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 2: Version Store Generation vs Cleanup Activity */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <RefreshCw className="w-4 h-4 text-emerald-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Version Store Generation & Cleanup (KB)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="KB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredVersionActivity}
                        onHoverSeries={setHoveredVersionActivity}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="version_gen_kb"
                    name="Version Gen Total (KB)"
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredVersionActivity, 'version_gen_kb')}
                    strokeWidth={hoveredVersionActivity === 'version_gen_kb' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="version_cleanup_kb"
                    name="Version Cleanup Total (KB)"
                    stroke="#ef4444"
                    strokeOpacity={getOpacity(hoveredVersionActivity, 'version_cleanup_kb')}
                    strokeWidth={hoveredVersionActivity === 'version_cleanup_kb' ? 2.5 : 1.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No version store rate telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Free Space & Longest Running Transaction */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* Graph 3: Free Space vs Total Used Space */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <HardDrive className="w-4 h-4 text-green-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Free vs Used Space (MB)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorFreeMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorUsedMb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="MB" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredFreeSpace}
                        onHoverSeries={setHoveredFreeSpace}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="free_mb"
                    name="Free Space (MB)"
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredFreeSpace, 'free_mb')}
                    fillOpacity={getOpacity(hoveredFreeSpace, 'free_mb') * 0.4}
                    fill="url(#colorFreeMb)"
                    strokeWidth={hoveredFreeSpace === 'free_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_used_mb"
                    name="Total Used Space (MB)"
                    stroke="#ef4444"
                    strokeOpacity={getOpacity(hoveredFreeSpace, 'total_used_mb')}
                    strokeWidth={hoveredFreeSpace === 'total_used_mb' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No free space telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 4: Longest Active Transaction */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Clock className="w-4 h-4 text-rose-400" />
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              Longest Active Transaction (Sec)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorLongestTxn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="Sec" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredTxn}
                        onHoverSeries={setHoveredTxn}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="longest_txn_sec"
                    name="Longest Txn (Sec)"
                    stroke="#f43f5e"
                    strokeOpacity={getOpacity(hoveredTxn, 'longest_txn_sec')}
                    fillOpacity={getOpacity(hoveredTxn, 'longest_txn_sec') * 0.4}
                    fill="url(#colorLongestTxn)"
                    strokeWidth={hoveredTxn === 'longest_txn_sec' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No transaction duration telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Dedicated Deep-Dives (Internal vs User Objects & Version Store vs Mixed Extents) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* User Objects */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Database className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
              User Objects (MB)
            </h4>
          </div>
          <div className="h-36 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '10px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="user_mb"
                    name="User Objects"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.25}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-[11px] text-gray-500 italic">No user object data</span>
            )}
          </div>
        </div>

        {/* Internal Objects */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <FileCode className="w-3.5 h-3.5 text-purple-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
              Internal Objects (MB)
            </h4>
          </div>
          <div className="h-36 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '10px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="internal_mb"
                    name="Internal Objects"
                    stroke="#a855f7"
                    fill="#a855f7"
                    fillOpacity={0.25}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-[11px] text-gray-500 italic">No internal object data</span>
            )}
          </div>
        </div>

        {/* Version Store */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Archive className="w-3.5 h-3.5 text-amber-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
              Version Store (MB)
            </h4>
          </div>
          <div className="h-36 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '10px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="version_store_mb"
                    name="Version Store"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.25}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-[11px] text-gray-500 italic">No version store data</span>
            )}
          </div>
        </div>

        {/* Mixed Extents */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
            <Layers className="w-3.5 h-3.5 text-cyan-400" />
            <h4 className="text-[11px] font-bold text-gray-200 uppercase tracking-wider">
              Mixed Extents (MB)
            </h4>
          </div>
          <div className="h-36 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} tickCount={3} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '10px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="mixed_extent_mb"
                    name="Mixed Extents"
                    stroke="#06b6d4"
                    fill="#06b6d4"
                    fillOpacity={0.25}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-[11px] text-gray-500 italic">No mixed extent data</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSSQLTempDBView;
